import { useState } from 'react';
import { useT } from '../i18n/useT';
import {
  loadPeriodicSettings,
  patternProblem,
  periodicTitle,
  savePeriodicSettings,
  type Period,
} from '../lib/periodicNotes';

const PERIODS: Period[] = ['daily', 'weekly', 'monthly'];

/**
 * Title pattern and folder for daily, weekly and monthly notes, inside Settings.
 *
 * Each field shows the title today's note would get, so the token language
 * (`YYYY`, `GGGG`, `[W]`…) can be learned by typing rather than by reading. A
 * pattern that would collapse periods into one note is shown with the reason and
 * not saved — the previous working pattern stays in effect until the new one is
 * valid, so a half-typed pattern never breaks "open today".
 */
export function PeriodicSettings() {
  const t = useT();
  const [values, setValues] = useState(
    () =>
      Object.fromEntries(PERIODS.map((p) => [p, loadPeriodicSettings(p)])) as Record<
        Period,
        { pattern: string; folder: string | null }
      >,
  );

  const update = (period: Period, patch: Partial<{ pattern: string; folder: string | null }>) => {
    const next = { ...values[period], ...patch };
    setValues((v) => ({ ...v, [period]: next }));
    savePeriodicSettings(period, next);
  };

  return (
    <section className="mb-periodic-settings" aria-labelledby="mb-periodic-heading">
      <h3 id="mb-periodic-heading">{t('periodic.heading')}</h3>
      <p className="mb-modal-note">{t('periodic.hint')}</p>
      {PERIODS.map((period) => {
        const { pattern, folder } = values[period];
        const problem = patternProblem(period, pattern);
        const id = `mb-periodic-${period}`;
        return (
          <fieldset key={period} className="mb-periodic-row">
            <legend>{t(`periodic.${period}`)}</legend>
            <label htmlFor={`${id}-pattern`}>{t('periodic.pattern')}</label>
            <input
              id={`${id}-pattern`}
              value={pattern}
              spellCheck={false}
              aria-invalid={problem !== null}
              aria-describedby={`${id}-preview`}
              onChange={(e) => update(period, { pattern: e.target.value })}
            />
            <label htmlFor={`${id}-folder`}>{t('periodic.folder')}</label>
            <input
              id={`${id}-folder`}
              value={folder ?? ''}
              placeholder={t('periodic.root')}
              onChange={(e) => update(period, { folder: e.target.value })}
            />
            <p
              id={`${id}-preview`}
              className={problem ? 'mb-modal-note mb-import-error' : 'mb-modal-note'}
              aria-live="polite"
            >
              {problem
                ? t(`periodic.problem.${problem}`)
                : t('periodic.preview', { title: periodicTitle(new Date(), { pattern, folder }) })}
            </p>
          </fieldset>
        );
      })}
    </section>
  );
}
